(function() {
  'use strict';

  /**
   * Returns the embed URL from a metafield value (string or object with .value / .url).
   * Supports:
   *  - direct URL string (starting with http)
   *  - full <iframe ... src="..."> HTML code
   */
  function getEmbedUrl(value) {
    if (value == null) return '';
    if (typeof value === 'object') {
      value = value.value ?? value.url ?? '';
    }
    if (typeof value !== 'string') return '';
    let str = value.trim();

    // If metafield contains full iframe HTML, extract the src
    if (str.toLowerCase().includes('<iframe')) {
      // Normal: src="https://..." or src='https://...'
      let match = str.match(/<iframe[^>]*src=[\"']([^\"']+)[\"'][^>]*>/i);
      if (match && match[1]) {
        const urlFromIframe = match[1].trim();
        if (urlFromIframe.indexOf('http') === 0) return urlFromIframe;
      }
      // Shopify sometimes stores with doubled quotes: src=""https://..."" 
      match = str.match(/<iframe[^>]*src=[\"']{2}(https?:\/\/[^\"']+)[\"']{2}/i);
      if (match && match[1]) {
        const urlFromIframe = match[1].trim();
        return urlFromIframe.indexOf('http') === 0 ? urlFromIframe : '';
      }
    }

    // Otherwise treat it as a direct URL if it starts with http
    return str.indexOf('http') === 0 ? str : '';
  }

  /**
   * Builds variant id -> embed URL map from the section script (so we don't rely on event.detail.variant.metafields).
   */
  function getVariantEmbedMap(sectionId) {
    const script = document.getElementById(`configurator-embed-variants-${sectionId}`);
    if (!script) return {};
    try {
      const list = JSON.parse(script.textContent);
      const map = {};
      if (Array.isArray(list)) {
        list.forEach(function (item) {
          if (item && item.id != null && item.url) {
            map[String(item.id)] = item.url;
          }
        });
      }
      return map;
    } catch (e) {
      return {};
    }
  }

  /**
   * Updates the configurator embed iframe based on the selected variant.
   * Uses variant->embed map from the section first, then event variant metafields, then product-level embed.
   */
  function updateConfiguratorEmbed(event) {
    const variant = event.detail.variant;
    if (!variant) return;

    const embedWrappers = document.querySelectorAll('.configurator-embed__wrapper[data-product-id]');
    embedWrappers.forEach((embedWrapper) => {
      const sectionId = embedWrapper.id.replace('configurator-embed-', '');
      const productId = embedWrapper.dataset.productId;

      if (productId && event.detail.formElement) {
        const formProductId = event.detail.formElement.closest('[data-product-id]')?.dataset.productId;
        if (formProductId && formProductId !== productId) return;
      }

      const productEmbedScript = document.getElementById(`product-configurator-embed-${sectionId}`);
      const productEmbed = productEmbedScript ? JSON.parse(productEmbedScript.textContent) : null;
      const productUrl = getEmbedUrl(productEmbed);

      const variantMap = getVariantEmbedMap(sectionId);
      const variantUrlFromMap = variantMap[String(variant.id)];
      const variantUrlFromEvent = getEmbedUrl(variant.metafields?.custom?.configurator_embed);
      const embedUrl = variantUrlFromMap || variantUrlFromEvent || productUrl;

      const iframe = embedWrapper.querySelector('.configurator-embed__iframe');
      const section = embedWrapper.closest('.configurator-embed-section');
      if (!iframe || !section) return;

      if (embedUrl) {
        iframe.src = embedUrl;
        section.classList.remove('configurator-embed-section--hidden');
      } else {
        iframe.removeAttribute('src');
        section.classList.add('configurator-embed-section--hidden');
      }
    });
  }

  function initConfiguratorEmbed() {
    const embedWrappers = document.querySelectorAll('.configurator-embed__wrapper[data-product-id]');
    embedWrappers.forEach((wrapper) => {
      const sectionId = wrapper.id.replace('configurator-embed-', '');
      const productForms = document.querySelectorAll('form[id^="product-form-"]');
      let variantSelects = null;
      let mainProductSectionId = null;

      productForms.forEach((form) => {
        if (!form.id || typeof form.id !== 'string') return;
        const formSectionId = form.id.replace('product-form-', '');
        const selects = document.querySelector(`variant-selects[data-section="${formSectionId}"], variant-radios[data-section="${formSectionId}"]`);
        if (selects) {
          variantSelects = selects;
          mainProductSectionId = formSectionId;
        }
      });

      let variant = null;
      if (variantSelects) {
        if (variantSelects.currentVariant) {
          variant = variantSelects.currentVariant;
        } else if (variantSelects.getVariantData && variantSelects.getVariantData().length > 0) {
          const variantData = variantSelects.getVariantData();
          const selectedVariantId = document.querySelector(`#product-form-${mainProductSectionId} input[name="id"]`)?.value;
          if (selectedVariantId) {
            variant = variantData.find(v => v.id === parseInt(selectedVariantId));
          }
          if (!variant && variantData.length > 0) {
            variant = variantData.find(v => v.available) || variantData[0];
          }
        }
      }

      if (variant) {
        updateConfiguratorEmbed({
          detail: {
            variant: variant,
            sectionId: sectionId,
            formElement: document.querySelector(`#product-form-${mainProductSectionId}`)
          }
        });
      } else {
        const productEmbedScript = document.getElementById(`product-configurator-embed-${sectionId}`);
        const productEmbed = productEmbedScript ? JSON.parse(productEmbedScript.textContent) : null;
        const productUrl = getEmbedUrl(productEmbed);
        const iframe = wrapper.querySelector('.configurator-embed__iframe');
        const section = wrapper.closest('.configurator-embed-section');
        const existingSrc = iframe ? (iframe.getAttribute('src') || '').trim() : '';
        if (iframe && section) {
          if (productUrl) {
            iframe.src = productUrl;
            section.classList.remove('configurator-embed-section--hidden');
          } else if (existingSrc && existingSrc.indexOf('http') === 0) {
            section.classList.remove('configurator-embed-section--hidden');
          } else {
            iframe.removeAttribute('src');
            section.classList.add('configurator-embed-section--hidden');
          }
        }
      }
    });
  }

  document.addEventListener('variant:change', updateConfiguratorEmbed);

  const run = () => setTimeout(initConfiguratorEmbed, 100);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  document.addEventListener('shopify:section:load', () => setTimeout(initConfiguratorEmbed, 100));
})();
